import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit3, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/crm";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function InsightsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    crmApi.listInsights()
      .then(setItems)
      .catch(() => toast.error("Failed to load articles"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await crmApi.deleteInsight(id);
      toast.success("Article deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const togglePublish = async (a) => {
    try {
      const next = a.status === "published" ? "draft" : "published";
      await crmApi.updateInsight(a.id, { ...a, status: next });
      toast.success(next === "published" ? "Published" : "Moved to draft");
      load();
    } catch { toast.error("Failed to update status"); }
  };

  return (
    <div className="space-y-6" data-testid="insights-admin">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] tracking-overline uppercase text-neutral-500">Content</div>
          <h1 className="font-serif text-3xl mt-1">Insights</h1>
          <p className="mt-1 text-sm text-neutral-500">Publish and manage bi-weekly briefings for the public website.</p>
        </div>
        <Link
          to="/crm/insights/new"
          data-testid="new-insight-btn"
          className="inline-flex items-center gap-2 px-5 py-2 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase hover:opacity-90 transition"
        >
          <Plus size={14}/> New Article
        </Link>
      </div>

      <div className="border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/50 text-[11px] tracking-overline uppercase text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Author</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Published</th>
              <th className="text-left px-4 py-3">Updated</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-400">
                No articles yet. <Link to="/crm/insights/new" className="underline">Publish your first briefing</Link>.
              </td></tr>
            ) : items.map((a) => (
              <tr key={a.id} className="border-t border-neutral-200 dark:border-neutral-800" data-testid={`insight-row-${a.slug}`}>
                <td className="px-4 py-3 max-w-md">
                  <div className="font-medium truncate">{a.title}</div>
                  <div className="text-xs text-neutral-500 truncate">/{a.slug}</div>
                </td>
                <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">{a.author_name}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] tracking-overline uppercase px-2 py-1 border ${
                    a.status === "published"
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800/50"
                      : "bg-neutral-100 text-neutral-700 border-neutral-300 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700"
                  }`}>{a.status}</span>
                </td>
                <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">{fmt(a.published_at)}</td>
                <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">{fmt(a.updated_at)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    {a.status === "published" && (
                      <a href={`/insights/${a.slug}`} target="_blank" rel="noreferrer" title="View live" data-testid={`insight-view-${a.slug}`} className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded">
                        <ExternalLink size={14}/>
                      </a>
                    )}
                    <button onClick={() => togglePublish(a)} title={a.status === "published" ? "Unpublish" : "Publish"} data-testid={`insight-toggle-${a.slug}`} className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded">
                      {a.status === "published" ? <EyeOff size={14}/> : <Eye size={14}/>}
                    </button>
                    <Link to={`/crm/insights/${a.id}`} title="Edit" data-testid={`insight-edit-${a.slug}`} className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded">
                      <Edit3 size={14}/>
                    </Link>
                    <button onClick={() => handleDelete(a.id)} title="Delete" data-testid={`insight-delete-${a.slug}`} className="p-1.5 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-600 rounded">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
